import axios from 'axios';
import Api from '../api/userApi';
import serviceWorkerUpdateManager from './serviceWorkerUpdate';

// Web Push Notification Service
class WebPushService {
  constructor() {
    this.permission = null;
    this.serviceWorkerRegistration = null;
    this.pushSubscription = null;
    this.vapidPublicKey = null;
    this._swMessageListenerAttached = false;
    this._swMessageHandler = null;
    this._lastRefreshTime = 0; // Throttle refresh calls
    this._initialized = false;  // Track if initialize() completed
    this._syncInFlight = null;  // Coalesce concurrent subscription syncs (login + init)
  }

  // Reset in-memory push state. Used on logout so a previous user's cached
  // subscription is never reused for the next user on the same browser.
  _resetState() {
    this.pushSubscription = null;
    this._initialized = false;
    this._lastRefreshTime = 0;
    this._syncInFlight = null;
  }

  static VAPID_STORAGE_KEY = 'ulcaapp:push:vapidPublicKey';

  _getStoredVapidKey() {
    try {
      return localStorage.getItem(WebPushService.VAPID_STORAGE_KEY) || '';
    } catch (e) {
      return '';
    }
  }

  _setStoredVapidKey(v) {
    try {
      localStorage.setItem(WebPushService.VAPID_STORAGE_KEY, v || '');
    } catch (e) {
      // ignore
    }
  }

  async requestPermission() {
    if (!('Notification' in window)) {
      console.warn('[WebPush] This browser does not support notifications');
      return false;
    }

    // Check current permission status
    const currentPermission = Notification.permission;
    this.permission = currentPermission;

    if (currentPermission === 'granted') {
      console.log('[WebPush] ✅ Notification permission already granted');
      return true;
    }

    if (currentPermission === 'denied') {
      console.warn('[WebPush] ⚠️ Notification permission denied. Please enable in browser settings.');
      console.warn('[WebPush] To enable: Browser Settings > Privacy > Site Settings > Notifications');
      return false;
    }

    try {
      console.log('[WebPush] Requesting notification permission...');
      const permission = await Notification.requestPermission();
      this.permission = permission;
      
      if (permission === 'granted') {
        console.log('[WebPush] ✅ Notification permission granted!');
      } else if (permission === 'denied') {
        console.warn('[WebPush] ❌ Notification permission denied by user');
      } else {
        console.warn('[WebPush] ⚠️ Notification permission default (user dismissed)');
      }
      
      return permission === 'granted';
    } catch (error) {
      console.error('[WebPush] Error requesting notification permission:', error);
      return false;
    }
  }

  // True only when this browser can actually do web push (also false on iOS
  // Safari tabs and in-app webviews that lack the APIs).
  isPushSupported() {
    return (
      typeof window !== 'undefined' &&
      'Notification' in window &&
      'serviceWorker' in navigator &&
      'PushManager' in window
    );
  }

  // 'unsupported' | 'default' | 'granted' | 'denied'
  // 'default' means we can still ask; 'denied' means the browser will no longer
  // show the native prompt until the user resets it in site settings.
  getPermissionState() {
    if (typeof window === 'undefined' || !('Notification' in window)) return 'unsupported';
    return Notification.permission;
  }

  /**
   * Resolve the live Service Worker registration WITHOUT hanging.
   *
   * The in-memory `serviceWorkerRegistration` is empty after a fresh page load
   * until initialize() runs, but the browser may already have an active SW +
   * push subscription. Reading the registration back from the browser lets any
   * caller (e.g. the settings toggle) reflect the true subscription state.
   *
   * Note: we deliberately avoid `navigator.serviceWorker.ready`, which never
   * resolves when no SW is registered and would leave the UI stuck loading.
   */
  async getServiceWorkerRegistration() {
    if (this.serviceWorkerRegistration) return this.serviceWorkerRegistration;
    if (!('serviceWorker' in navigator)) return null;
    try {
      const reg = await navigator.serviceWorker.getRegistration();
      if (reg) this.serviceWorkerRegistration = reg;
      return reg || null;
    } catch (_) {
      return null;
    }
  }

  /**
   * Source of truth for the settings toggle: reads the ACTUAL browser
   * permission + push subscription (not React/local state). Never throws.
   *
   * @returns {Promise<{supported: boolean, permission: string, subscribed: boolean}>}
   */
  async getDeviceSubscriptionStatus() {
    const supported = this.isPushSupported();
    const permission = this.getPermissionState();

    // Only a granted permission can have an active subscription. When the
    // permission isn't granted, there is definitively no subscription.
    if (!supported || permission !== 'granted') {
      if (permission !== 'granted') this.pushSubscription = null;
      return { supported, permission, subscribed: false };
    }

    try {
      const registration = await this.getServiceWorkerRegistration();
      if (!registration?.pushManager) {
        return { supported, permission, subscribed: false };
      }
      const subscription = await registration.pushManager.getSubscription();
      this.pushSubscription = subscription || null;
      return { supported, permission, subscribed: !!subscription };
    } catch (_) {
      return { supported, permission, subscribed: false };
    }
  }

  /**
   * Single entry point for turning on notifications. MUST be called directly
   * from a user gesture (click/tap) — Safari requires it, and Chrome will
   * otherwise demote the request to its silent "quieter" UI.
   *
   * Requests permission first, then registers the SW, subscribes, and binds the
   * subscription to the current user on the server.
   *
   * @returns {Promise<{ok: boolean, reason?: string}>}
   */
  async enableNotificationsFromUserGesture() {
    if (!this.isPushSupported()) {
      return { ok: false, reason: 'unsupported' };
    }

    // Ask for permission synchronously inside the gesture.
    const granted = await this.requestPermission();
    if (!granted) {
      // 'denied' (blocked) or 'default' (dismissed without choosing)
      return { ok: false, reason: this.getPermissionState() };
    }

    try {
      if (!this.serviceWorkerRegistration) {
        await this.registerServiceWorker();
      }
      const result = await this.ensureBrowserSubscription();
      if (!result?.subscription) {
        return { ok: false, reason: 'subscribe-failed' };
      }

      // Sync with the server BEFORE reporting success, so the toggle only turns
      // ON once the backend actually holds this subscription.
      const serverResp = await this.sendSubscriptionToServer(result.subscription);
      if (!serverResp) {
        // Roll back a subscription we JUST created so the toggle stays a truthful
        // OFF (never show ON without a server-synced subscription). A pre-existing
        // subscription is left intact — it was already bound previously and a
        // later sync will re-bind it.
        if (result.created) {
          try {
            await result.subscription.unsubscribe();
          } catch (_) {
            // ignore
          }
          this.pushSubscription = null;
        }
        return { ok: false, reason: 'server-sync-failed' };
      }

      // The browser reused an endpoint the server had marked dead (expired
      // 404/410). Reactivating it is not enough — the endpoint itself is stale —
      // so regenerate a genuinely fresh one and sync it as active before we
      // report the toggle ON.
      if (serverResp.wasReactivated) {
        console.warn('[WebPush] enable: server reactivated a previously-dead endpoint — regenerating a fresh subscription');
        const fresh = await this._regenerateSubscription('enable-endpoint-reactivated');
        if (!fresh) {
          return { ok: false, reason: 'server-sync-failed' };
        }
      }

      this._initialized = true;
      this._lastRefreshTime = Date.now();
      return { ok: true };
    } catch (error) {
      console.error('[WebPush] enableNotificationsFromUserGesture failed:', error);
      return { ok: false, reason: 'error' };
    }
  }

  async registerServiceWorker() {
    // NOTE: We register the Service Worker in development too. `sw.js` itself
    // detects localhost / port 3000 and disables ALL caching + fetch handling
    // there (it only handles `push`), so there's no hot-reload conflict — but it
    // still lets notifications be tested locally (localhost is a secure context).
    if ('serviceWorker' in navigator) {
      try {
        // Try to get existing registration from update manager first
        let registration = serviceWorkerUpdateManager.getRegistration();

        // If no registration from update manager, register directly
        if (!registration) {
          console.log('[WebPush] Registering service worker at /sw.js...');
          registration = await navigator.serviceWorker.register('/sw.js', {
            scope: '/',
            updateViaCache: 'none'
          });
        }

        this.serviceWorkerRegistration = registration;
        console.log('[WebPush] Service Worker registered:', registration);
        
        // Wait for service worker to be ready
        await navigator.serviceWorker.ready;
        console.log('[WebPush] Service Worker is ready');
        
        // Check if service worker is active
        if (registration.active) {
          console.log('[WebPush] Service Worker is active');
        } else if (registration.installing) {
          console.log('[WebPush] Service Worker is installing...');
          await new Promise((resolve) => {
            registration.installing.addEventListener('statechange', () => {
              if (registration.installing.state === 'activated') {
                console.log('[WebPush] Service Worker installed and activated');
                resolve();
              }
            });
          });
        }
        
        return registration;
      } catch (error) {
        console.error('[WebPush] Service Worker registration failed:', error);
        // Service worker is optional - only log if it's not a 404 or MIME type error
        if (!error.message.includes('MIME type') && !error.message.includes('404') && !error.message.includes('Failed to register')) {
          console.warn('[WebPush] Service Worker registration failed (non-critical):', error.message);
        }
        return null;
      }
    }
    console.warn('[WebPush] Service Workers not supported in this browser');
    return null;
  }

  async getVapidPublicKey() {
    if (this.vapidPublicKey) {
      return this.vapidPublicKey;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(Api.pushVapidPublicKey, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      this.vapidPublicKey = response.data.publicKey;
      return this.vapidPublicKey;
    } catch (error) {
      console.error('Error fetching VAPID public key:', error);
      return null;
    }
  }

  /**
   * Ensure THIS browser has a live push subscription — reusing an existing one
   * (so we never create duplicates) and creating a fresh one only when needed.
   * Does NOT talk to the server.
   *
   * @returns {Promise<{subscription: PushSubscription, created: boolean} | null>}
   */
  async ensureBrowserSubscription() {
    // Get service worker registration
    if (!this.serviceWorkerRegistration) {
      await this.registerServiceWorker();
    }
    if (!this.serviceWorkerRegistration) {
      console.warn('[WebPush] Cannot subscribe: Service Worker not available');
      return null;
    }

    // Get VAPID public key
    const publicKey = await this.getVapidPublicKey();
    if (!publicKey) {
      console.error('[WebPush] Cannot subscribe: VAPID public key not available');
      return null;
    }
    const applicationServerKey = this.urlBase64ToUint8Array(publicKey);

    // Always read the live subscription from the pushManager (not the possibly
    // stale in-memory reference) so we reuse it instead of duplicating.
    let subscription = await this.serviceWorkerRegistration.pushManager.getSubscription();
    let created = false;
    if (!subscription) {
      console.log('[WebPush] Subscribing to push manager...');
      subscription = await this.serviceWorkerRegistration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey
      });
      created = true;
    } else {
      console.log('[WebPush] Reusing existing browser push subscription');
    }

    this.pushSubscription = subscription;
    return { subscription, created };
  }

  async subscribeToPush() {
    try {
      const result = await this.ensureBrowserSubscription();
      if (!result?.subscription) return null;

      console.log('[WebPush] Push subscription ready — syncing to server before reporting success...');
      const serverResp = await this.sendSubscriptionToServer(result.subscription);

      // Report success ONLY after the server confirms it stored this subscription
      // as active. Without this gate the caller (settings toggle, recovery flow,
      // login sync) would treat push as working while the backend holds no live
      // endpoint — so no push, and no failed-push retry, would ever be delivered.
      if (!serverResp) {
        console.warn('[WebPush] ❌ Push subscription NOT confirmed — server sync failed');
        // Roll back a subscription we JUST created so we never leave the browser
        // holding an endpoint the server never stored. A pre-existing subscription
        // is left intact — a later sync will re-bind it.
        if (result.created) {
          try {
            await result.subscription.unsubscribe();
          } catch (_) {
            // ignore
          }
          this.pushSubscription = null;
        }
        return null;
      }

      this.pushSubscription = result.subscription;
      console.log('[WebPush] ✅ Push subscription active and synced to server');
      return result.subscription;
    } catch (error) {
      console.error('[WebPush] Error subscribing to push notifications:', error);
      if (error.name === 'NotAllowedError') {
        console.warn('[WebPush] Push subscription denied by user');
      } else if (error.name === 'AbortError') {
        console.warn('[WebPush] Push subscription aborted (push service unavailable)');
      }
      return null;
    }
  }

  /**
   * Recover from an expired/invalid push endpoint (404/410) on both mobile PWA
   * and desktop browser.
   *
   * A stored endpoint becomes invalid when the push service expires it (typically
   * during a long idle gap — e.g. between a booking's original and extended end
   * time) or when the OS kills the mobile browser in the background. When that
   * happens the server marks the subscription inactive and the push send is
   * recorded as `failed` on the notification.
   *
   * This forcibly releases any stale browser endpoint, mints a genuinely fresh
   * PushSubscription, and syncs it to the server as ACTIVE. Once the server holds
   * a live endpoint again, the existing failed-push retry scheduler re-delivers
   * any pending failed pushes on its next sweep. Recovery only (re)creates the
   * device subscription — it never creates notifications or emits the bell — so
   * there are no duplicate bell alerts and no duplicate notifications.
   *
   * @returns {Promise<PushSubscription|null>} the fresh, server-synced subscription
   */
  async _regenerateSubscription(reason = 'recovery') {
    console.warn(`[WebPush] 🔁 Recovering push subscription (reason: ${reason}) — obtaining a fresh endpoint...`);

    // Release any stale browser endpoint first so pushManager.subscribe() mints a
    // genuinely new one. Re-syncing a dead endpoint would just fail again (410).
    try {
      const existing =
        this.pushSubscription ||
        (await this.serviceWorkerRegistration?.pushManager?.getSubscription?.());
      if (existing) {
        await existing.unsubscribe();
        console.log('[WebPush] Released stale browser push endpoint before recovery');
      }
    } catch (e) {
      console.warn('[WebPush] Could not release stale endpoint (continuing):', e?.message || e);
    }
    this.pushSubscription = null;

    // Creates a brand-new browser subscription AND syncs it to the server; only
    // returns non-null once the server has stored it as active.
    const sub = await this.subscribeToPush();
    if (sub) {
      console.log('[WebPush] ✅ Subscription recovery complete — fresh endpoint synced to server as active. Pending failed pushes will be delivered by the retry scheduler.');
    } else {
      console.warn('[WebPush] ❌ Subscription recovery failed — server does not yet hold a live endpoint for this device (will retry on next refresh)');
    }
    return sub;
  }

  /**
   * Persist this browser's subscription on the server for the currently
   * logged-in user (POST /api/push-notifications/subscribe). This is what
   * transfers endpoint OWNERSHIP to the current user on a shared device
   * (User A → User B), so it must reliably complete: a transient network/5xx/429
   * failure would otherwise leave the endpoint bound to the previous user.
   *
   * We therefore retry on TRANSIENT failures (no response / 5xx / 429) with a
   * short backoff. We do NOT retry on definitive client errors (4xx other than
   * 429) or when there is no auth token — retrying those can never succeed.
   *
   * @param {Object} [opts]
   * @param {number} [opts.retries=3] - Extra attempts after the first on transient failures.
   * @param {number} [opts.timeoutMs=15000] - Per-request timeout.
   */
  async sendSubscriptionToServer(subscription, { retries = 3, timeoutMs = 15000 } = {}) {
    const token = localStorage.getItem('token');
    if (!token) {
      console.warn('[WebPush] Cannot send subscription: User not authenticated');
      return null;
    }

    let subscriptionObject;
    let payload;
    try {
      subscriptionObject = subscription.toJSON();
      payload = {
        endpoint: subscriptionObject.endpoint,
        keys: {
          p256dh: subscriptionObject.keys.p256dh,
          auth: subscriptionObject.keys.auth
        },
        userAgent: navigator.userAgent,
        deviceInfo: `${navigator.platform} - ${navigator.userAgent}`
      };
    } catch (error) {
      console.error('[WebPush] Cannot serialize subscription for server sync:', error?.message || error);
      return null;
    }

    console.log('[WebPush] 💾 Saving/updating subscription on server for the currently logged-in user...', {
      endpoint: `${subscriptionObject.endpoint.substring(0, 50)}...`
    });

    let lastError;
    for (let attempt = 0; attempt <= retries; attempt += 1) {
      try {
        const response = await axios.post(Api.pushSubscribe, payload, {
          headers: { Authorization: `Bearer ${token}` },
          timeout: timeoutMs
        });

        console.log('[WebPush] ✅ Subscription saved/updated on server (bound to current user):', {
          subscriptionId: response.data?.subscription?.id,
          wasReactivated: response.data?.wasReactivated,
          attempt: attempt + 1
        });
        return response.data;
      } catch (error) {
        lastError = error;
        const status = error.response?.status;
        // Retry only on transient conditions: network error (no status), server
        // errors (5xx), or rate limiting (429). Everything else is definitive.
        const isTransient = !status || status >= 500 || status === 429;

        if (!isTransient || attempt === retries) {
          break;
        }

        const delayMs = 1000 * (attempt + 1);
        console.warn(`[WebPush] Subscription server sync failed transiently (status ${status || 'network'}), retrying in ${delayMs}ms (attempt ${attempt + 1}/${retries + 1})`);
        await new Promise((resolve) => setTimeout(resolve, delayMs));
      }
    }

    console.error('[WebPush] Error sending subscription to server (giving up after retries):', lastError?.message || lastError);
    if (lastError?.response) {
      console.error('[WebPush] Server response:', lastError.response.data);
    }
    return null;
  }

  // Diagnostic: fetch server-side push status (/push-notifications/status)
  async fetchServerStatus() {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        return { ok: false, error: 'Not authenticated' };
      }
      const response = await axios.get(Api.pushStatus, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 10000
      });
      return { ok: true, data: response.data };
    } catch (error) {
      return {
        ok: false,
        error: error?.response?.data?.message || error.message || 'Unknown error'
      };
    }
  }

  async unsubscribeFromPush() {
    try {
      // The in-memory reference is empty after a fresh page load, so resolve the
      // live subscription from the Service Worker before giving up. This lets a
      // "turn off on this device" toggle work even when we didn't create the
      // subscription in this session.
      let subscription = this.pushSubscription;
      if (!subscription && 'PushManager' in window && 'serviceWorker' in navigator) {
        try {
          const registration =
            this.serviceWorkerRegistration ||
            (await navigator.serviceWorker.getRegistration());
          if (registration?.pushManager) {
            subscription = await registration.pushManager.getSubscription();
          }
        } catch (_) {
          // ignore
        }
      }

      if (!subscription) {
        this.pushSubscription = null;
        return;
      }

      const endpoint = subscription.endpoint;

      // Release the browser push endpoint. Notify the server regardless of the
      // unsubscribe() return value so a "turn off" always reaches the backend.
      try {
        await subscription.unsubscribe();
      } catch (e) {
        console.warn('[WebPush] pushManager.unsubscribe() failed (continuing):', e?.message || e);
      }

      const token = localStorage.getItem('token');
      if (token && endpoint) {
        try {
          await axios.post(Api.pushUnsubscribe, {
            endpoint
          }, {
            headers: {
              Authorization: `Bearer ${token}`
            }
          });
        } catch (error) {
          console.error('Error notifying server of unsubscribe:', error);
        }
      }

      // Always clear the in-memory reference so re-enabling starts clean.
      this.pushSubscription = null;
      console.log('Unsubscribed from push notifications');
    } catch (error) {
      console.error('Error unsubscribing from push notifications:', error);
      // Even on unexpected errors, drop the stale reference.
      this.pushSubscription = null;
    }
  }

  urlBase64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  }

  async showNotification(title, options = {}) {
    // Do NOT prompt here — only show if the user has already granted permission
    // via the explicit opt-in flow. Prompting from a background code path would
    // be jarring and gets suppressed by the browser anyway.
    if (this.getPermissionState() !== 'granted') {
      return;
    }

    const resolveTag = (opts) => {
      const tag = typeof opts?.tag === 'string' ? opts.tag.trim() : '';
      if (tag) return tag;

      const data = opts?.data && typeof opts.data === 'object' && !Array.isArray(opts.data) ? opts.data : {};
      const type = (data.type || 'notification').toString();
      const notificationId = data.notificationId ? data.notificationId.toString() : '';
      const bookingId = data.bookingId ? data.bookingId.toString() : '';
      const jobRequestId = data.jobRequestId ? data.jobRequestId.toString() : '';

      if (notificationId) return `${type}-${notificationId}`;
      if (bookingId) return `${type}-booking-${bookingId}-${Date.now()}`;
      if (jobRequestId) return `${type}-jobRequest-${jobRequestId}-${Date.now()}`;
      return `${type}-${Date.now()}`;
    };

    const notificationOptions = {
      body: options.body || '',
      icon: options.icon || '/android-chrome-192x192.png',
      badge: options.badge || '/android-chrome-192x192.png',
      tag: resolveTag(options),
      requireInteraction: !!options.requireInteraction,
      data: options.data || {},
      actions: options.actions || [],
      ...options
    };

    if (this.serviceWorkerRegistration) {
      // Use service worker for better control
      this.serviceWorkerRegistration.showNotification(title, notificationOptions);
    } else {
      // Fallback to regular notification
      // The Notification constructor does NOT support actions. Remove them to avoid runtime errors.
      const { actions, ...simpleOptions } = notificationOptions;
      new Notification(title, simpleOptions);
    }
  }

  /**
   * Lightweight subscription refresh — meant to be called whenever the app
   * returns to the foreground (visibilitychange → visible, window `focus`, or
   * `pageshow`/bfcache restore) and on every socket (re)connect. On mobile the
   * OS can kill the browser process while backgrounded, which invalidates the
   * push endpoint; this re-checks the current browser subscription and
   * re-registers it with the server so push delivery keeps working across devices.
   *
   * Throttled to at most once every 60 seconds to avoid excessive network calls.
   * Pass `{ force: true }` for high-signal recovery events (e.g. the Service
   * Worker's `pushsubscriptionchange`) that must re-sync immediately instead of
   * being suppressed by a routine foreground refresh that just ran.
   *
   * @param {Object} [opts]
   * @param {boolean} [opts.force=false] - Bypass the 60s throttle.
   */
  async refreshSubscription({ force = false } = {}) {
    // Throttle: at most once every 60 seconds unless `force` is set. (Lowered
    // from 5 min so a passive user's endpoint is re-validated more often whenever
    // the app is open, which keeps later notifications — e.g. "work completed" /
    // "payment" — deliverable.)
    const now = Date.now();
    if (!force && now - this._lastRefreshTime < 60 * 1000) {
      return;
    }
    this._lastRefreshTime = now;
    if (force) {
      console.log('[WebPush] Subscription refresh requested (force) — bypassing throttle');
    }

    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      if (!('PushManager' in window)) return;
      // Bootstrap the SW registration if it isn't set yet (e.g. refresh triggered
      // by socket connect before initialize() finished).
      if (!this.serviceWorkerRegistration) {
        await this.registerServiceWorker();
      }
      if (!this.serviceWorkerRegistration) return;

      const currentSubscription = await this.serviceWorkerRegistration.pushManager.getSubscription();

      if (!currentSubscription) {
        // Browser lost the subscription (e.g., after data clear, SW update, or the
        // OS killed the mobile browser in the background). Recover a fresh one.
        console.log('[WebPush] No browser subscription found on refresh — recovering');
        await this._regenerateSubscription('refresh-missing-subscription');
        return;
      }

      // Re-register the current subscription with the server.
      // If the server says it was previously inactive (dead/expired 404/410
      // endpoint), force a fresh endpoint from the push service so later pushes —
      // and any pending failed pushes picked up by the retry scheduler — deliver.
      const serverResponse = await this.sendSubscriptionToServer(currentSubscription);

      if (serverResponse?.wasReactivated) {
        console.warn('[WebPush] Refresh: server reports the stored endpoint was dead (expired 404/410) — regenerating a fresh endpoint');
        await this._regenerateSubscription('refresh-endpoint-expired');
      } else if (serverResponse) {
        this.pushSubscription = currentSubscription;
      } else {
        // Server sync failed (network/auth). Keep the current browser subscription
        // and let the next refresh re-attempt — do not drop a possibly-good endpoint.
        console.warn('[WebPush] Refresh: server sync failed — keeping current browser subscription; will retry on next refresh');
      }
    } catch (error) {
      // Refresh is best-effort; never let it break the app
      console.warn('[WebPush] Subscription refresh error (non-fatal):', error?.message || error);
    }
  }

  /**
   * Ensure the CURRENT browser's push subscription is saved/updated for the
   * currently logged-in user. Safe (and intended) to call on EVERY login.
   *
   * This always reads the latest browser subscription from the Service Worker
   * and (re)binds it to the active account on the server, so a previous user's
   * account can never keep this device's subscription. Normal logout no longer
   * deactivates the subscription, so a routine logout→login (same OR different
   * user) simply reassigns endpoint ownership via /subscribe — the existing
   * endpoint is reused and NO fresh subscription is generated. A fresh
   * subscription is generated only when the endpoint is genuinely dead
   * (expired 404/410) or the VAPID key rotated.
   *
   * Concurrent calls (login handler + SocketContext initialize firing together)
   * are coalesced into a single in-flight run.
   */
  async syncSubscriptionForUser({ reason = 'login' } = {}) {
    if (this._syncInFlight) {
      console.log('[WebPush] Subscription sync already in progress — awaiting existing run...');
      return this._syncInFlight;
    }

    this._syncInFlight = (async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        console.warn('[WebPush] Cannot sync subscription: user not authenticated');
        return null;
      }

      console.log(`[WebPush] 🔔 Syncing push subscription for the current user (reason: ${reason})...`);

      // Ask for notification permission on login/register/init. If the state is
      // still 'default', this shows the native Allow/Deny prompt; if it's
      // already 'granted' it resolves immediately; if 'denied' it returns
      // without re-prompting (the browser suppresses it anyway).
      //
      // NOTE: The soft pre-prompt (NotificationOptIn) and the Profile
      // Notifications card remain fully intact — they use
      // enableNotificationsFromUserGesture() and are the recommended path on
      // iOS/Safari (which require a user gesture) and after a "Later"/denied.
      const hasPermission = await this.requestPermission();
      if (!hasPermission) {
        console.warn('[WebPush] Cannot sync subscription: notification permission not granted');
        return null;
      }

      if (!this.serviceWorkerRegistration) {
        await this.registerServiceWorker();
      }
      if (!this.serviceWorkerRegistration || !('PushManager' in window)) {
        console.warn('[WebPush] Cannot sync subscription: Service Worker / PushManager unavailable');
        return null;
      }

      const publicKey = await this.getVapidPublicKey();
      if (!publicKey) {
        console.warn('[WebPush] Cannot sync subscription: VAPID public key unavailable');
        return null;
      }

      const prevVapidKey = this._getStoredVapidKey();
      const vapidKeyChanged = !!(prevVapidKey && prevVapidKey !== publicKey);
      if (vapidKeyChanged) {
        console.warn('[WebPush] Detected VAPID public key change; re-subscribing on this device.');
      }

      // Always read the LATEST subscription from the Service Worker.
      let subscription = await this.serviceWorkerRegistration.pushManager.getSubscription();

      // Drop the old subscription if the VAPID key rotated — it can no longer
      // receive pushes and must be replaced with a fresh one.
      if (subscription && vapidKeyChanged) {
        try {
          await subscription.unsubscribe();
          console.log('[WebPush] Unsubscribed old subscription after VAPID rotation');
        } catch (e) {
          console.warn('[WebPush] Failed to unsubscribe old subscription (continuing):', e?.message || e);
        }
        subscription = null;
      }

      // No usable browser subscription — create a fresh one and bind it.
      if (!subscription) {
        console.log('[WebPush] No browser subscription found — generating a new one...');
        this.pushSubscription = null;
        subscription = await this.subscribeToPush(); // creates + sends to server
        if (subscription) {
          this._setStoredVapidKey(publicKey);
          console.log('[WebPush] ✅ New push subscription generated and bound to current user');
        } else {
          console.warn('[WebPush] ❌ Failed to generate push subscription');
        }
        return subscription;
      }

      // Existing browser subscription — (re)bind it to the CURRENT logged-in user.
      this.pushSubscription = subscription;
      console.log('[WebPush] Existing browser subscription found — (re)binding to current user...');
      const serverResponse = await this.sendSubscriptionToServer(subscription);

      // Only regenerate when the endpoint was genuinely DEAD (the server had
      // marked it inactive after an expired 404/410 while idle). Normal logout no
      // longer deactivates a subscription, so a routine logout→login (same or
      // different user) keeps the existing endpoint and simply rebinds its
      // ownership via /subscribe above — no regeneration, no duplicate
      // subscriptions.
      if (serverResponse?.wasReactivated) {
        console.warn('[WebPush] Endpoint was previously inactive (expired 404/410) — regenerating a fresh subscription for the current user');
        subscription = await this._regenerateSubscription('login-endpoint-reactivated');
      }

      this._setStoredVapidKey(publicKey);
      console.log('[WebPush] ✅ Push subscription is bound to the current logged-in user');
      return subscription;
    })();

    try {
      return await this._syncInFlight;
    } finally {
      this._syncInFlight = null;
    }
  }

  /**
   * Handle logout WITHOUT tearing down web push.
   *
   * Push notifications intentionally SURVIVE logout: the browser PushSubscription
   * and its server-side record (user + endpoint + keys + isActive) are left fully
   * intact so the user keeps receiving notifications while logged out and even
   * when the PWA is closed. This method ONLY drops this tab's in-memory
   * references, so the next user who logs in on this browser re-reads the live
   * subscription and rebinds its ownership to their account via /subscribe.
   *
   * IMPORTANT: We deliberately do NOT here:
   *   - call subscription.unsubscribe()
   *   - call POST /api/push-notifications/unsubscribe
   *   - set the DB record isActive = false
   *   - delete the DB record
   *
   * Deactivating/unsubscribing is reserved for the EXPLICIT
   * "turn off notifications on this device" action (unsubscribeFromPush()),
   * which is a different user intent than logging out.
   */
  async clearSubscriptionForLogout() {
    console.log('[WebPush] 🚪 Logout: retaining push subscription (survives logout); resetting in-memory state only');
    this._resetState();
  }

  async initialize() {
    // Check if user is authenticated
    const token = localStorage.getItem('token');
    if (!token) {
      console.log('[WebPush] User not authenticated, skipping push notification setup');
      return;
    }

    console.log('[WebPush] Initializing web push notifications...');

    if (!('serviceWorker' in navigator)) {
      console.warn('[WebPush] Service workers not supported');
      return;
    }

    const registration = await this.registerServiceWorker();
    if (!registration) {
      console.warn('[WebPush] Service worker registration failed');
      return;
    }

    // Bridge Service Worker messages -> CustomEvent for React app.
    // Attach only once to avoid duplicate toasts across re-logins / re-renders.
    if (!this._swMessageListenerAttached && navigator.serviceWorker?.addEventListener) {
      this._swMessageHandler = (event) => {
        try {
          if (!event?.data) return;

          if (event.data.type === 'PUSH_NOTIFICATION') {
            const payload = event.data.payload;
            window.dispatchEvent(new CustomEvent('ulcaapp:push', { detail: payload }));
            return;
          }

          // The Service Worker fired `pushsubscriptionchange` (the browser expired
          // or rotated the push endpoint) and asked open pages to re-sync. Force an
          // immediate refresh so the AUTHENTICATED subscribe path re-binds the fresh
          // endpoint to the current user. This complements the SW's own token-less
          // /refresh call (keyed only by the old endpoint), and is the recovery
          // path used whenever the app happens to be open.
          if (event.data.type === 'PUSH_SUBSCRIPTION_CHANGED') {
            console.log('[WebPush] SW reported pushsubscriptionchange — forcing subscription refresh');
            this.refreshSubscription({ force: true }).catch(() => {});
            return;
          }
        } catch (e) {
          // ignore
        }
      };

      navigator.serviceWorker.addEventListener('message', this._swMessageHandler);
      this._swMessageListenerAttached = true;
    }

    // Bind the current browser's subscription to the currently logged-in user.
    await this.syncSubscriptionForUser({ reason: 'initialize' });

    // Mark initialization complete so refreshSubscription() can run
    this._initialized = true;
    this._lastRefreshTime = Date.now();
  }
}

// Create singleton instance
const webPushService = new WebPushService();

// Diagnostic function to check push notification status
webPushService.getStatus = async function() {
  const status = {
    supported: 'serviceWorker' in navigator && 'PushManager' in window,
    permission: Notification.permission,
    serviceWorkerRegistered: !!this.serviceWorkerRegistration,
    pushSubscription: null,
    vapidPublicKey: this.vapidPublicKey,
    errors: []
  };

  try {
    const registration = await this.getServiceWorkerRegistration();
    status.serviceWorkerRegistered = !!registration;
    if (registration?.pushManager) {
      const subscription = await registration.pushManager.getSubscription();
      status.pushSubscription = subscription ? {
        endpoint: subscription.endpoint.substring(0, 50) + '...',
        keys: !!subscription.getKey('p256dh')
      } : null;
    }
  } catch (error) {
    status.errors.push(`Error checking subscription: ${error.message}`);
  }

  return status;
};

export default webPushService;

